import * as React from "react";
import { Card, CardBody, CardHeader, PageContainer } from "../components/Card";
import { PageHeader, SectionHeader } from "../components/PageHeader";
import { Button, IconButton } from "../components/Button";
import { ConfirmModal } from "../components/ConfirmModal";
import { Field, Input, Checkbox } from "../components/Form";
import { Table, Thead, Tbody, Tr, Th, Td, TdActions } from "../components/Table";
import { Badge } from "../components/Badge";
import { Banner, ListStates } from "../components/States";
import { IdBadge } from "../components/Detail";
import { useAsync, useAction } from "../general/useAsync";
import { useToast } from "../general/Toast";
import { usePermission } from "../account/AccountStore";
import { Permissions } from "../account/permissions";
import { Integration, LoadAssessmentDataParameters } from "../api/APIClient";
import { integrationsClient, scadsysIntegrationClient } from "../api/clients";

/**
 * External system connections, and the one-shot import from the legacy SCADSYS system.
 *
 * The legacy import is deliberately behind a confirmation with the URL and school id spelled back:
 * it pulls a whole school's assessment data over SOAP and upserts it, keyed on `Source`+`SourceID`
 * rather than primary ids. Pointing it at the wrong school is not something to discover afterwards.
 */

export const IntegrationsIndex: React.FC = () => {
    const canManage = usePermission(Permissions.INTEGRATIONS_MANAGE);
    const canExecute = usePermission(Permissions.INTEGRATIONS_EXECUTE);
    const canImport = usePermission(Permissions.SCADSYS_IMPORT);

    return (
        <PageContainer>
            <PageHeader
                title="Integrations"
                icon={["fad", "plug"]}
                description="Connections to external systems, and the legacy SCADSYS data import."
            />

            <IntegrationList canManage={canManage} canExecute={canExecute} />

            {canImport && <ScadsysImport />}
        </PageContainer>
    );
};

const IntegrationList: React.FC<{ canManage: boolean; canExecute: boolean }> = ({ canManage, canExecute }) => {
    const toast = useToast();
    const [deleting, setDeleting] = React.useState<Integration | null>(null);

    const integrations = useAsync(() => integrationsClient.getIntegrations(), [], { initial: [] as Integration[] });

    const remove = useAction(async () => {
        if (!deleting) return;
        await integrationsClient.deleteIntegration(deleting.id ?? "");
        toast.success("Integration deleted.");
        setDeleting(null);
        integrations.refresh();
    });

    return (
        <>
            <Card>
                <CardHeader>
                    <SectionHeader title="Configured integrations" description="Scheduled connections to external systems." />
                </CardHeader>

                <ListStates
                    isLoading={integrations.isLoading}
                    error={integrations.error}
                    onRetry={integrations.refresh}
                    isEmpty={(integrations.data ?? []).length === 0}
                    empty={{
                        icon: ["fad", "plug"],
                        title: "No integrations configured",
                        hint: "Integrations are created by the platform team rather than from this screen.",
                    }}
                >
                    <Table>
                        <Thead>
                            <Tr>
                                <Th>Name</Th>
                                <Th>Type</Th>
                                <Th>Trigger</Th>
                                <Th>Last run</Th>
                                <Th align="right">Runs</Th>
                                <Th align="right">Actions</Th>
                            </Tr>
                        </Thead>
                        <Tbody>
                            {(integrations.data ?? []).map((integration) => (
                                <IntegrationRow
                                    key={integration.id}
                                    integration={integration}
                                    canManage={canManage}
                                    canExecute={canExecute}
                                    onChanged={integrations.refresh}
                                    onDelete={() => setDeleting(integration)}
                                />
                            ))}
                        </Tbody>
                    </Table>
                </ListStates>
            </Card>

            <ConfirmModal
                isOpen={deleting !== null}
                onClose={() => setDeleting(null)}
                onConfirm={remove.run}
                title="Delete integration"
                icon={["fad", "trash"]}
                confirmLabel="Delete"
                busy={remove.busy}
                error={remove.error}
                body={
                    <>
                        Delete <strong>{deleting?.name}</strong>? Its schedule stops and its run history goes with it. This cannot be
                        undone.
                    </>
                }
            />
        </>
    );
};

const IntegrationRow: React.FC<{
    integration: Integration;
    canManage: boolean;
    canExecute: boolean;
    onChanged: () => void;
    onDelete: () => void;
}> = ({ integration, canManage, canExecute, onChanged, onDelete }) => {
    const toast = useToast();
    const id = integration.id ?? "";

    const run = useAction(async () => {
        await integrationsClient.startIntegrationRun(id);
        toast.success("Integration run started.");
        onChanged();
    });

    const toggleTrigger = useAction(async () => {
        if (integration.triggerEnabled) {
            await integrationsClient.disableTrigger(id);
            toast.success("Trigger disabled.");
        } else {
            await integrationsClient.enableTrigger(id);
            toast.success("Trigger enabled.");
        }
        onChanged();
    });

    return (
        <Tr>
            <Td>
                <span className="font-medium text-text-strong">{integration.name}</span>
                <div>
                    <IdBadge id={integration.id} />
                </div>
            </Td>
            <Td>
                {/* IntegrationType is a ScadsysCore Enumeration, so it arrives as an object. */}
                <Badge tone="neutral">{integration.integrationType?.displayName ?? integration.integrationType?.name ?? "—"}</Badge>
            </Td>
            <Td>
                <Badge tone={integration.triggerEnabled ? "success" : "neutral"}>
                    {integration.triggerEnabled ? "Enabled" : "Disabled"}
                </Badge>
            </Td>
            <Td>
                {integration.triggerLastRun ? (
                    new Date(integration.triggerLastRun).toLocaleString()
                ) : (
                    <span className="text-text-weak">Never</span>
                )}
            </Td>
            <Td align="right">{integration.integrationRuns?.length ?? 0}</Td>
            <TdActions>
                {canExecute && (
                    <IconButton icon={["fad", "play"]} label={`Run ${integration.name}`} onClick={run.run} loading={run.busy} />
                )}
                {canManage && (
                    <>
                        <IconButton
                            icon={["fad", integration.triggerEnabled ? "pause" : "clock"]}
                            label={integration.triggerEnabled ? "Disable trigger" : "Enable trigger"}
                            onClick={toggleTrigger.run}
                            loading={toggleTrigger.busy}
                        />
                        <IconButton icon={["fad", "trash"]} variant="outline-danger" label={`Delete ${integration.name}`} onClick={onDelete} />
                    </>
                )}
            </TdActions>
        </Tr>
    );
};

/**
 * The legacy SCADSYS import.
 *
 * Pulls assessment data for one school over SOAP and upserts it. Reconciliation is keyed on
 * `Source` + `SourceID`, not primary ids, so re-running it against the same school updates rather
 * than duplicates - but running it against the *wrong* school writes that school's data into this
 * organisation, which is why it is behind a confirmation that repeats what is about to happen.
 */
const ScadsysImport: React.FC = () => {
    const toast = useToast();
    const [url, setUrl] = React.useState("");
    const [schoolID, setSchoolID] = React.useState("");
    const [includeStructure, setIncludeStructure] = React.useState(true);
    const [confirming, setConfirming] = React.useState(false);

    const load = useAction(async () => {
        await scadsysIntegrationClient.loadAssessmentData(
            new LoadAssessmentDataParameters({ url: url.trim(), schoolID: schoolID.trim(), includeAssessmentStructure: includeStructure })
        );
        toast.success("Assessment data imported.");
        setConfirming(false);
    });

    const ready = !!(url.trim() && schoolID.trim());

    return (
        <>
            <Card className="mt-6">
                <CardHeader>
                    <SectionHeader
                        title="Import from legacy SCADSYS"
                        description="Pulls a school's assessment data over SOAP and upserts it into this organisation."
                    />
                </CardHeader>
                <CardBody>
                    <Banner tone="warning" className="mb-4">
                        This writes into the organisation you are currently signed in to. Records are matched on their source identity, so
                        re-importing the same school updates rather than duplicates - but importing the <em>wrong</em> school mixes another
                        school&rsquo;s data into this one.
                    </Banner>

                    <div className="grid gap-4 sm:grid-cols-2">
                        <Field label="Legacy endpoint URL" required className="mb-0">
                            <Input value={url} placeholder="https://..." onChange={(e) => setUrl(e.target.value)} />
                        </Field>
                        <Field label="School id" required className="mb-0">
                            <Input value={schoolID} placeholder="Legacy school identifier" onChange={(e) => setSchoolID(e.target.value)} />
                        </Field>
                    </div>

                    <Checkbox
                        className="mt-4"
                        label="Include assessment structure"
                        hint="Also imports the group tree and assessments, not just enrollment and marks."
                        checked={includeStructure}
                        onChange={(e) => setIncludeStructure(e.target.checked)}
                    />

                    <div className="mt-4">
                        <Button icon={["fad", "download"]} onClick={() => setConfirming(true)} disabled={!ready}>
                            Import
                        </Button>
                    </div>

                    {load.error && (
                        <Banner tone="danger" className="mt-3">
                            {load.error}
                        </Banner>
                    )}
                </CardBody>
            </Card>

            <ConfirmModal
                isOpen={confirming}
                onClose={() => setConfirming(false)}
                onConfirm={load.run}
                title="Import legacy assessment data"
                icon={["fad", "download"]}
                confirmVariant="warning"
                confirmLabel="Import"
                busy={load.busy}
                error={load.error}
                body={
                    <>
                        <p>
                            Import school <strong>{schoolID}</strong> from <strong>{url}</strong>
                            {includeStructure ? ", including its assessment structure" : ""}?
                        </p>
                        <p className="mt-2">
                            This writes into the organisation you are signed in to. Check the school id is the right one before continuing.
                        </p>
                    </>
                }
            />
        </>
    );
};

export default IntegrationsIndex;
